import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, Select, Spin, theme } from "antd";
import { FilterOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import useBusinessResource from "../hooks/useBusinessResource";
import "./stock-workspace.css";

export function useStockWorkspaceTheme() {
  const { token } = theme.useToken();
  const dark = ["#000", "#000000"].includes(token.colorBgBase);
  return {
    "--stock-mode": dark ? "dark" : "light",
    "--stock-surface": token.colorBgContainer, "--stock-text": token.colorText,
    "--stock-heading": dark ? token.colorTextHeading : token.colorInfoTextActive,
    "--stock-label": dark ? token.colorTextSecondary : token.colorInfoText,
    "--stock-muted": token.colorTextSecondary, "--stock-border": token.colorBorderSecondary,
    "--stock-band": dark ? token.colorFillAlter : `color-mix(in srgb, ${token.blue1} 65%, ${token.colorBgContainer})`,
    "--stock-success": token.colorSuccessText, "--stock-danger": token.colorErrorText,
    "--stock-warning": token.colorWarningText,
  };
}

export function StockPageHeader({ description, children }) {
  return <header className="stock-page-header"><div><h1>Gestion du stock</h1><p>{description}</p></div>
    <div className="stock-page-actions">{children}</div></header>;
}

export function StockSummary({ items, loading, unavailable }) {
  return <section className="stock-summary" aria-label="Résumé des résultats" aria-busy={loading}>
    {items.map(item => <div key={item.label} className={`stock-summary-item stock-tone-${item.tone || "info"}`}>
      {item.icon}<div><span>{item.label}</span><div className="stock-summary-value">
        <strong>{unavailable || item.value === undefined || item.value === null ? "—" : Number(item.value).toLocaleString("fr-FR")}</strong>
        {item.help && <small>{item.help}</small>}</div></div></div>)}
    {loading && <Spin size="small" className="stock-summary-loading" />}
  </section>;
}

export function StockReadNotice({ error, retry, loading, hasData = false }) {
  return error ? <Alert type="warning" showIcon className="stock-read-notice"
    message={error} description={hasData ? "Les derniers résultats disponibles sont conservés."
      : "Les données ne sont pas disponibles. Réessayez le chargement."}
    action={<Button onClick={retry} loading={loading}>Réessayer</Button>} /> : null;
}

export function StockFilterField({ label, children }) {
  return <div className="stock-filter-field"><span>{label}</span>{children}</div>;
}

export function StockFilters({ searchLabel, placeholder, value, onChange, onApply, onReset, children }) {
  return <form className="stock-filters" onSubmit={event => { event.preventDefault(); onApply(); }}>
    <div className="stock-filter-primary">
      <StockFilterField label={searchLabel}><Input aria-label={searchLabel} prefix={<SearchOutlined />} allowClear
        value={value.search || ""} placeholder={placeholder} onChange={event => onChange({ ...value, search: event.target.value })} /></StockFilterField>
      <StockFilterField label="Période"><DatePicker.RangePicker aria-label="Période" format="DD/MM/YYYY"
        value={value.period || null} placeholder={["Date de début", "Date de fin"]}
        onChange={period => onChange({ ...value, period })} /></StockFilterField>
    </div>
    <div className="stock-filter-secondary">{children}<div className="stock-filter-buttons">
      <Button type="primary" htmlType="submit" icon={<FilterOutlined aria-hidden />}>Appliquer</Button>
      <Button icon={<ReloadOutlined aria-hidden />} onClick={onReset}>Réinitialiser</Button>
    </div></div>
  </form>;
}

// Choix historiques, isolés par utilisateur/entreprise/établissement : aucun
// droit de gestion des produits ou utilisateurs n'est requis pour filtrer.
export function StockRemoteSelect({ endpoint, params, kind, companyId, warehouseId, value, onChange, placeholder, label }) {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [settled, setSettled] = useState("");
  const paramsKey = JSON.stringify(params || {});
  const stableParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);
  useEffect(() => { setSearch(""); setSettled(""); }, [companyId, warehouseId, paramsKey, kind]);
  useEffect(() => { const timer = setTimeout(() => setSettled(search), 300); return () => clearTimeout(timer); }, [search]);
  const resource = useBusinessResource({ userId: user?.id, companyId, warehouseId, scopeLevel: "company",
    resource: "stock-filter-options", parameters: [endpoint, paramsKey, kind, settled, value || null],
    enabled: Boolean(companyId), active: true,
    load: async signal => {
      const response = await axios.get(endpoint, { signal, headers: { "X-Company-Id": String(companyId) },
        params: { ...stableParams, filter_kind: kind, filter_search: settled, selected_value: value || undefined } });
      return response.data;
    } });
  return <StockFilterField label={label}><Select aria-label={label} allowClear showSearch
    value={value || undefined} placeholder={placeholder} filterOption={false} searchValue={search} onSearch={setSearch}
    onChange={onChange} loading={resource.loading} options={(resource.data?.filter_options || []).map(option => ({ ...option, value: Number(option.value) }))}
    notFoundContent={resource.loading ? <Spin size="small" /> : resource.error ? "Liste indisponible" : "Aucun résultat"}
    dropdownRender={menu => <>{menu}{resource.error && <Button type="link" onClick={() => { void resource.refresh().catch(() => {}); }}>Réessayer</Button>}
      {resource.data?.has_more && <div className="stock-select-help">Affinez la recherche pour voir d’autres choix.</div>}</>}
  /></StockFilterField>;
}

export function useStockListRefresh(refresh) {
  useEffect(() => {
    const onRefresh = () => { void refresh(); };
    window.addEventListener("focus", onRefresh); window.addEventListener("online", onRefresh);
    return () => { window.removeEventListener("focus", onRefresh); window.removeEventListener("online", onRefresh); };
  }, [refresh]);
}

export const dateParams = (period, start = "start_date", end = "end_date") => period?.[0] && period?.[1]
  ? { [start]: period[0].format("YYYY-MM-DD"), [end]: period[1].format("YYYY-MM-DD") } : {};
